import { CreateSubscriptionContractBackofficeInputSchema, CreateSubscriptionContractInputSchema, } from "../schemas.js";
import { getSubscriptionProduct } from "../../subscription-product/services/getSubscriptionProduct.js";
import { compareDataFactoryData } from "../../customer/services/compareDataFactoryData.js";
import { getContractItemMetaFields, getContractMetaFields, } from "../selectors/meta.js";
import { CONTRACT_PENDING_STATUS, getContractPaymentInput, PAYMENT_PROPERTY, PAYMENT_PROVIDERS, } from "../../payment/index.js";
import Logger from "@cappelendamm/logger";
import { resolveNextRenewAtForBookClub, resolveNextRenewAtForBookSubscription, resolveNextRenewAtForSeries, } from "../utils/resolveNextRenewAt.js";
import { SOURCE_VALUES, SUBSCRIPTION_CONTRACT_META_KEY } from "../../index.js";
import { isSubscriptionShapeBookClub } from "../../subscription-plan/selectors/isSubscriptionShape.js";
const buildPendingSnapshotJson = (customer) => {
    return JSON.stringify({
        firstName: customer.firstName ?? "",
        lastName: customer.lastName ?? "",
        birthDate: customer.birthDate ?? "",
        phone: customer.phone ?? "",
        email: customer.email ?? "",
        billing: {
            street: customer.addresses?.billing?.street ?? "",
            postalCode: customer.addresses?.billing?.postalCode ?? "",
            city: customer.addresses?.billing?.city ?? "",
        },
        delivery: {
            street: customer.addresses?.delivery?.street ?? "",
            postalCode: customer.addresses?.delivery?.postalCode ?? "",
            city: customer.addresses?.delivery?.city ?? "",
        },
    });
};
const getContractPayment = ({ identifier, netsUnscheduledSubscriptionId, match, isImported, customer, nonMatchingFields, }) => {
    const isUserVerificationNeeded = match === "noMatch";
    const isNetsPayment = identifier === PAYMENT_PROVIDERS.NETS;
    const paymentProperties = {
        ...(isUserVerificationNeeded &&
            !isImported && {
            // Pending fields on payment, removed on approval
            [PAYMENT_PROPERTY.CONTRACT.CONTRACT_PENDING_STATUS]: CONTRACT_PENDING_STATUS.USER_VERIFICATION_NEEDED,
            [PAYMENT_PROPERTY.CONTRACT.PENDING_SNAPSHOT]: buildPendingSnapshotJson(customer),
            [PAYMENT_PROPERTY.CONTRACT.PENDING_NON_MATCHING_FIELDS]: nonMatchingFields.join(","),
        }),
        ...(isNetsPayment && {
            [PAYMENT_PROPERTY.SHARED.NETS_UNSCHEDULED_SUBSCRIPTION_ID]: netsUnscheduledSubscriptionId,
        }),
    };
    return getContractPaymentInput({
        provider: identifier,
        properties: paymentProperties,
    });
};
export const getSignUpSubscriptionContractData = async (input, signUpSubscriptionContractOptions = {
    isImported: false,
    sendEmail: true,
}, customerIdentifier, { client }, selectedBookSku, customerInDataFactory) => {
    Logger.log(`Validating and mapping signup input data for customer id: ${customerIdentifier} `);
    const { isImported, sendEmail } = signUpSubscriptionContractOptions;
    const shouldUseBackofficeSchema = isImported || input.meta.source === SOURCE_VALUES.CUSTOMER_SERVICE;
    if (shouldUseBackofficeSchema) {
        CreateSubscriptionContractBackofficeInputSchema.parse(input);
    }
    else {
        CreateSubscriptionContractInputSchema.parse(input);
    }
    const { customer, product, paymentMethod, freight: { initialSku, recurringSku }, meta, } = input;
    const subscription = await getSubscriptionProduct({ sku: product.sku }, { client });
    const { period: subscriptionPlanPeriod, initialPrice, recurringPrice, name, sku, isSeries, signUpPrice, freight: freightOptions, } = subscription;
    if (!sku)
        throw new Error(`No subscription found for sku: ${product.sku}`);
    const initialFreight = freightOptions?.initial.find(({ sku }) => sku === initialSku);
    const recurringFreight = freightOptions?.recurring.find(({ sku }) => sku === recurringSku);
    if (!initialFreight)
        throw new Error(`Freight ${initialSku} is not matching freight options in subscription ${sku}`);
    if (!recurringFreight)
        throw new Error(`Freight ${recurringSku} is not matching freight options in subscription ${sku}`);
    let match = "noData";
    let nonMatchingFields = [];
    if (customerInDataFactory) {
        try {
            const comparisonResult = await compareDataFactoryData({
                customer: input.customer,
                dataFactoryUser: customerInDataFactory,
            });
            match = comparisonResult.match;
            nonMatchingFields = (comparisonResult.nonMatchingFields ??
                []);
            Logger.log(`[signup] DataFactory compare result: ${match} (non-matching fields: ${nonMatchingFields.join(", ")}) for customer id: ${customerIdentifier}`);
        }
        catch (e) {
            Logger.error("[signup] DataFactory compare failed:", e);
        }
    }
    else if (isImported) {
        Logger.log("[signup] Skip DataFactory lookup: imported contract.");
    }
    else {
        Logger.log("[signup] Skip DataFactory compare - no DataFactory user found");
    }
    const contractPayment = getContractPayment({
        ...paymentMethod,
        match,
        isImported,
        customer,
        nonMatchingFields,
    });
    let renewAt;
    let dispatchBatchCode;
    const isBookClub = isSubscriptionShapeBookClub(subscription.type);
    const hasGifts = subscription.gifts.count > 0;
    const providedRenewAt = input.status?.renewAt;
    if (isSeries) {
        const initialBook = selectedBookSku || input.meta?.initialSku;
        const nextBook = subscription.series?.products.find((book) => book.sku === initialBook);
        renewAt = providedRenewAt ?? resolveNextRenewAtForSeries(nextBook);
    }
    else if (isBookClub) {
        const renewAtResult = resolveNextRenewAtForBookClub(hasGifts, subscription.dispatches, { productSku: subscription.sku });
        renewAt = renewAtResult.renewAt;
        dispatchBatchCode = renewAtResult.selectedDispatch?.batchCode;
    }
    else {
        renewAt = resolveNextRenewAtForBookSubscription(hasGifts, subscription.dispatches);
    }
    const contractItemMeta = getContractItemMetaFields(meta).filter(({ key }) => key !== SUBSCRIPTION_CONTRACT_META_KEY.DISPATCH_BATCH_CODE);
    if ((!signUpPrice || !recurringPrice) && !isBookClub) {
        throw new Error(`Missing subscription plan price for sku: ${product.sku}`);
    }
    // Book club does not define prices in the subscription plan, 0 NOK is used as a
    // placeholder to satisfy Crystallize API requirements. Actual pricing is handled in backend.
    const recurringContractPrice = recurringPrice ?? {
        price: 0,
        currency: "NOK",
    };
    const sendEmailMeta = {
        key: SUBSCRIPTION_CONTRACT_META_KEY.SEND_EMAIL,
        value: String(!!sendEmail),
    };
    const dispatchBatchCodeMeta = isBookClub && dispatchBatchCode
        ? [
            {
                key: SUBSCRIPTION_CONTRACT_META_KEY.DISPATCH_BATCH_CODE,
                value: dispatchBatchCode,
            },
        ]
        : [];
    const contractMeta = [
        ...getContractMetaFields(meta).filter(({ key }) => key !== SUBSCRIPTION_CONTRACT_META_KEY.DISPATCH_BATCH_CODE),
        ...dispatchBatchCodeMeta,
    ];
    Logger.log(`Mapped signup input data for customer id: ${customerIdentifier} and product sku: ${product.sku}`);
    const contract = {
        customerIdentifier,
        item: {
            name: name ?? "",
            sku: product.sku,
            meta: contractItemMeta,
        },
        status: {
            activeUntil: renewAt,
            renewAt,
        },
        subscriptionPlan: {
            periodId: product?.subscriptionPlanPeriodId ?? subscriptionPlanPeriod.id,
            periodName: subscriptionPlanPeriod.name ?? "",
            identifier: product.planIdentifier,
        },
        payment: contractPayment,
        initial: initialPrice,
        recurring: recurringContractPrice,
        addresses: [
            { type: "billing", ...customer.addresses.billing },
            { type: "delivery", ...customer.addresses.delivery },
        ],
        meta: [...contractMeta, sendEmailMeta],
    };
    return { contract, product };
};
